'use client'

import { Card } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { deleteProjectAction, stopProjectAction, toggleAutoDeployAction } from "@/app/actions/projects"
import { DeployDialog } from "./deploy-dialog"
import { SettingsDialog } from "./settings-dialog"
import { useState } from "react"
import { Loader2, Trash2, Info, Copy, Check, Rocket, CircleStop, Play, Github, Terminal, RefreshCw, GitCommit, Settings } from "lucide-react"

interface Project {
    name: string;
    type?: 'local' | 'github';
    repoUrl?: string;
    branch: string;
    status: string;
    envVars?: string;
    lastError?: string;
    autoDeployOnCommit?: boolean;
    lastCommitHash?: string;
    lastCommitMessage?: string;
    lastCommitDate?: string;
    startedAt?: string;
    url?: string | null;
}

// Format time ago from startedAt date
function formatTimeAgo(startedAt: string | undefined): string | null {
    if (!startedAt) return null
    const start = new Date(startedAt).getTime()
    const now = Date.now()
    const diff = Math.floor((now - start) / 1000)

    if (diff < 60) return `${diff} seconds ago`
    if (diff < 120) return '1 minute ago'
    if (diff < 3600) return `${Math.floor(diff / 60)} minutes ago`
    if (diff < 7200) return '1 hour ago'
    if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`
    if (diff < 172800) return '1 day ago'
    return `${Math.floor(diff / 86400)} days ago`
}

interface ProjectCardProps {
    project: Project
}

// Convert HTTPS URL to SSH clone URL
function getSSHUrl(repoUrl: string | undefined): string {
    if (!repoUrl) return ''
    // https://github.com/owner/repo.git -> git@github.com:owner/repo.git
    const match = repoUrl.match(/github\.com\/([^/]+\/[^/]+?)(?:\.git)?$/)
    if (match) {
        return `git@github.com:${match[1]}.git`
    }
    return repoUrl
}

// Get GitHub web URL
function getGitHubUrl(repoUrl: string | undefined): string {
    if (!repoUrl) return ''
    // https://github.com/owner/repo.git -> https://github.com/owner/repo
    return repoUrl.replace(/\.git$/, '')
}

export function ProjectCard({ project }: ProjectCardProps) {
    const isLocal = project.type === 'local'
    const statusColor =
        project.status === "running" ? "bg-green-500" :
        project.status === "failed" ? "bg-red-500" :
        project.status === "stopped" ? "bg-gray-500" :
        project.status === "restarting" ? "bg-blue-500" :
        "bg-yellow-500"
    const startedAgo = project.status === 'running' ? formatTimeAgo(project.startedAt) : null
    const [deleting, setDeleting] = useState(false)
    const [stopping, setStopping] = useState(false)
    const [errorDialogOpen, setErrorDialogOpen] = useState(false)
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
    const [deployDialogOpen, setDeployDialogOpen] = useState(false)
    const [startDialogOpen, setStartDialogOpen] = useState(false)
    const [watchDialogOpen, setWatchDialogOpen] = useState(false)
    const [settingsDialogOpen, setSettingsDialogOpen] = useState(false)
    const [copied, setCopied] = useState(false)
    const [copiedSSH, setCopiedSSH] = useState(false)
    const [autoDeployEnabled, setAutoDeployEnabled] = useState(project.autoDeployOnCommit ?? false)
    const [togglingAutoDeploy, setTogglingAutoDeploy] = useState(false)

    const sshUrl = getSSHUrl(project.repoUrl)
    const githubUrl = getGitHubUrl(project.repoUrl)

    const handleCopySSH = async () => {
        await navigator.clipboard.writeText(sshUrl)
        setCopiedSSH(true)
        setTimeout(() => setCopiedSSH(false), 2000)
    }

    const handleCopyError = async () => {
        if (project.lastError) {
            await navigator.clipboard.writeText(project.lastError)
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        }
    }

    const handleDelete = async () => {
        setDeleting(true)
        setDeleteDialogOpen(false)
        try {
            await deleteProjectAction(project.name)
        } catch (error) {
            console.error("Delete failed", error)
        } finally {
            setDeleting(false)
        }
    }

    const handleStop = async () => {
        setStopping(true)
        try {
            await stopProjectAction(project.name)
        } catch (error) {
            console.error("Stop failed", error)
        } finally {
            setStopping(false)
        }
    }

    const hasFailed = project.status === 'failed' && project.lastError
    const isDeploying = project.status === 'deploying'
    const isClickable = hasFailed || isDeploying

    const handleBadgeClick = () => {
        if (hasFailed) {
            setErrorDialogOpen(true)
        } else if (isDeploying) {
            setWatchDialogOpen(true)
        }
    }

    const handleAutoDeployToggle = async (checked: boolean) => {
        setTogglingAutoDeploy(true)
        try {
            await toggleAutoDeployAction(project.name, checked)
            setAutoDeployEnabled(checked)
        } catch (error) {
            console.error("Failed to toggle auto-deploy", error)
        } finally {
            setTogglingAutoDeploy(false)
        }
    }

    return (
        <TooltipProvider delayDuration={300}>
            <Card className="p-4 overflow-hidden">
                <div className="space-y-3">
                    {/* Top row: Name + Badge */}
                    <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                                <span className="font-medium text-lg truncate">{project.name}</span>
                                {isLocal && (
                                    <Badge variant="secondary" className="text-xs shrink-0">Local</Badge>
                                )}
                                {!isLocal && project.repoUrl && (
                                    <div className="flex items-center gap-1 shrink-0">
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <a href={githubUrl} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground">
                                                    <Github className="h-4 w-4" />
                                                </a>
                                            </TooltipTrigger>
                                            <TooltipContent>Open on GitHub</TooltipContent>
                                        </Tooltip>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <button onClick={handleCopySSH} className="text-muted-foreground hover:text-foreground">
                                                    {copiedSSH ? <Check className="h-4 w-4 text-green-500" /> : <Terminal className="h-4 w-4" />}
                                                </button>
                                            </TooltipTrigger>
                                            <TooltipContent>{copiedSSH ? 'Copied!' : 'Copy SSH URL'}</TooltipContent>
                                        </Tooltip>
                                    </div>
                                )}
                            </div>
                            {/* URL */}
                            {project.url && (
                                <a href={project.url} target="_blank" rel="noopener noreferrer" className="text-sm text-muted-foreground hover:underline truncate mt-1 block">
                                    {project.url.replace('https://', '')}
                                </a>
                            )}
                            {/* Branch and commit info */}
                            {!isLocal && (
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <div className="flex items-center gap-1.5 cursor-default text-xs text-muted-foreground/70 mt-0.5">
                                            <span className="shrink-0">{project.branch}</span>
                                            {project.lastCommitHash && (
                                                <>
                                                    <span className="text-muted-foreground/30">·</span>
                                                    <GitCommit className="h-3 w-3 shrink-0" />
                                                    <span className="font-mono">{project.lastCommitHash}</span>
                                                    {project.lastCommitMessage && (
                                                        <span className="truncate max-w-[200px]">— {project.lastCommitMessage}</span>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </TooltipTrigger>
                                    <TooltipContent side="bottom" className="max-w-xs">
                                        <p className="text-xs">Branch: {project.branch}</p>
                                        {project.lastCommitHash && (
                                            <>
                                                <p className="font-mono text-xs mt-1">{project.lastCommitHash}</p>
                                                <p className="text-xs mt-1">{project.lastCommitMessage}</p>
                                                {project.lastCommitDate && (
                                                    <p className="text-xs text-muted-foreground mt-1">{new Date(project.lastCommitDate).toLocaleString()}</p>
                                                )}
                                            </>
                                        )}
                                    </TooltipContent>
                                </Tooltip>
                            )}
                            {/* Started time - show when running */}
                            {startedAgo && (
                                <p className="text-xs text-muted-foreground/70 mt-0.5">
                                    Started {startedAgo}
                                </p>
                            )}
                        </div>
                        {/* Status badge - top right */}
                        <div className="shrink-0">
                            <Badge
                                variant="outline"
                                className={`${statusColor} text-white border-0 ${isClickable ? 'cursor-pointer hover:opacity-80' : ''}`}
                                onClick={isClickable ? handleBadgeClick : undefined}
                            >
                                {isDeploying && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                                {project.status === 'restarting' && <RefreshCw className="mr-1 h-3 w-3 animate-spin" />}
                                {project.status}
                                {hasFailed && <Info className="ml-1 h-3 w-3" />}
                            </Badge>
                        </div>
                    </div>

                    {/* Bottom row: Actions - right aligned, bigger buttons */}
                    <div className="flex items-center gap-2 flex-wrap justify-end">
                        {!isLocal && (
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        size="sm"
                                        variant={autoDeployEnabled ? "default" : "outline"}
                                        className="h-9 gap-2"
                                        onClick={() => handleAutoDeployToggle(!autoDeployEnabled)}
                                        disabled={togglingAutoDeploy}
                                    >
                                        {togglingAutoDeploy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                                        <span className="hidden sm:inline">Auto</span>
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>Auto-deploy {autoDeployEnabled ? 'on' : 'off'}</TooltipContent>
                            </Tooltip>
                        )}
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button size="sm" variant="outline" className="h-9 gap-2" onClick={() => setDeployDialogOpen(true)} disabled={project.status === 'deploying'}>
                                    {project.status === 'deploying' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />}
                                    <span className="hidden sm:inline">{project.status === 'running' ? 'Redeploy' : 'Deploy'}</span>
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>{project.status === 'running' ? 'Redeploy' : 'Deploy'}</TooltipContent>
                        </Tooltip>
                        {(project.status === 'stopped' || project.status === 'failed') && (
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button size="sm" variant="secondary" className="h-9 gap-2" onClick={() => setStartDialogOpen(true)}>
                                        <Play className="h-4 w-4" />
                                        <span className="hidden sm:inline">Start</span>
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>Start</TooltipContent>
                            </Tooltip>
                        )}
                        {(project.status === 'running' || project.status === 'restarting') && (
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button size="sm" variant="secondary" className="h-9 gap-2" onClick={handleStop} disabled={stopping}>
                                        {stopping ? <Loader2 className="h-4 w-4 animate-spin" /> : <CircleStop className="h-4 w-4" />}
                                        <span className="hidden sm:inline">Stop</span>
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>Stop</TooltipContent>
                            </Tooltip>
                        )}
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button size="sm" variant="outline" className="h-9 gap-2" onClick={() => setSettingsDialogOpen(true)}>
                                    <Settings className="h-4 w-4" />
                                    <span className="hidden sm:inline">Settings</span>
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>Settings</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button size="sm" variant="outline" className="h-9 gap-2 text-destructive hover:text-destructive border-destructive/50" onClick={() => setDeleteDialogOpen(true)} disabled={deleting}>
                                    {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                                    <span className="hidden sm:inline">Delete</span>
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>Delete</TooltipContent>
                        </Tooltip>
                    </div>
                </div>
            </Card>

            {/* Error Dialog */}
            <Dialog open={errorDialogOpen} onOpenChange={setErrorDialogOpen}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>Deploy Error</DialogTitle>
                    </DialogHeader>
                    <pre className="p-4 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded text-xs text-red-700 dark:text-red-300 overflow-x-auto whitespace-pre-wrap max-h-96 overflow-y-auto">
                        {project.lastError}
                    </pre>
                    <div className="flex justify-end">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleCopyError}
                            className="gap-2"
                        >
                            {copied ? (
                                <>
                                    <Check className="h-4 w-4" />
                                    Copied
                                </>
                            ) : (
                                <>
                                    <Copy className="h-4 w-4" />
                                    Copy Error
                                </>
                            )}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete Project</AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to delete <strong>{project.name}</strong>? 
                            This will remove the project folder and all associated data. This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} className="bg-destructive text-white hover:bg-destructive/90">
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Deploy Dialog */}
            <DeployDialog
                open={deployDialogOpen}
                onOpenChange={setDeployDialogOpen}
                project={project}
                mode="deploy"
            />

            {/* Start Dialog */}
            <DeployDialog
                open={startDialogOpen}
                onOpenChange={setStartDialogOpen}
                project={project}
                mode="start"
            />

            {/* Watch Deployment Dialog */}
            <DeployDialog
                open={watchDialogOpen}
                onOpenChange={setWatchDialogOpen}
                project={project}
                mode="watch"
            />

            {/* Settings Dialog */}
            <SettingsDialog
                open={settingsDialogOpen}
                onOpenChange={setSettingsDialogOpen}
                project={project}
            />
        </TooltipProvider>
    )
}
